import { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Descriptions } from 'antd';
import FileUpload from '../../../FileUpload';
import Button from '../../../Button';
import SelectSpeakingItemModal from '../../../modals/SelectSpeakingItemModal';
import useNotification from '../../../../hooks/useNotification';
import {
  ISpeakingPaper,
  ISpeakingPaperAttachFile,
  ISpeakingRubricListItem,
} from '../../../../models/newSpeaking.model';
import { CgCloseR } from 'react-icons/cg';
import { useHistory } from 'react-router-dom';
import styled from 'styled-components';
import useAuth from '../../../../hooks/useAuth';

type FormFile = File | null;

interface IProps {
  title: string;
  data?: ISpeakingPaper;
  speakingExamCode: string;
  speakingPartCode: string;

  submit(form: FormData): void;
}

const Image = styled.img`
  max-width: 500px;
  max-height: 500px;
`;

const SpeakingTestPartEditor: FC<IProps> = ({
  title,
  data,
  speakingExamCode,
  speakingPartCode,
  submit,
}) => {
  const { t } = useTranslation();
  const { writeAuthYn } = useAuth();
  const history = useHistory();

  const { infoNotification } = useNotification();

  const [partName, setPartName] = useState('');
  const [direction, setDirection] = useState('');
  const [files, setFiles] = useState<FormFile[]>([]);
  const [rubric, setRubric] = useState<ISpeakingRubricListItem | undefined>();
  const [visible, setVisible] = useState(false);
  const [attachFiles, setAttachFiles] = useState<ISpeakingPaperAttachFile[]>(
    [],
  );

  const onOk = (newRubric: ISpeakingRubricListItem) => {
    setRubric(newRubric);
    onCancel();
  };

  const onCancel = () => setVisible(false);

  const onSubmit = () => {
    if (!partName) return infoNotification(t('cbt.speakRubric.message1'));
    if (!direction) return infoNotification(t('cbt.speakTest.message7'));
    if (files.length < 1 && !data)
      return infoNotification(t('cbt.speakTest.message8'));
    if (!rubric) return infoNotification(t('cbt.speakTest.message9'));

    const form = new FormData();

    if (data) {
      form.append('speakingExamCode', speakingExamCode);
      form.append('speakingPartCode', speakingPartCode);
      form.append('rubricNo', String(rubric.rubricNo));
      form.append('partName', partName);
      form.append('direction', direction);

      if (data.attachFiles?.length)
        form.append(
          'attachFileCode',
          String(data.attachFiles[0].attachFileCode),
        );
      if (attachFiles?.length) {
        let hasNewFiles = false;
        files.forEach((value) => {
          if (value) hasNewFiles = true;
        });

        let hasDeleted = false;
        let isAllDeleted = true;
        attachFiles.forEach(({ originalName, fileSeq }) => {
          if (originalName) {
            isAllDeleted = false;
          } else {
            hasDeleted = true;
            form.append('deleteAttachFiles', String(fileSeq));
          }
        });
        if (hasNewFiles) form.append('attachFileStatus', 'update');
        else if (isAllDeleted) form.append('attachFileStatus', 'delete');
        else if (hasDeleted) form.append('attachFileStatus', 'update');
        else form.append('attachFileStatus', 'keep');
      } else {
        const realFiles = files.filter((item) => item !== null) as File[];
        if (files.length > 0) {
          form.append('attachFileStatus', 'create');
          realFiles.forEach((item) => form.append('attachNewFiles', item));
        } else {
          form.append('attachFileStatus', 'keep');
        }
      }
    } else {
      form.append('speakingExamCode', speakingExamCode);
      form.append('speakingPartCode', speakingPartCode);
      form.append('rubricNo', String(rubric.rubricNo));
      form.append('partName', partName);
      form.append('direction', direction);
      for (const file of files) if (file) form.append('attachFiles', file);
    }

    submit(form);
  };

  const onSetFile = (index: number, file: File) => {
    const newFiles = [...files];
    newFiles[index] = file;
    setFiles(newFiles);
  };

  const onAddFile = () => {
    const newFiles = [...files];
    newFiles.push(null);
    setFiles(newFiles);
  };

  const onDeleteFile = (index: number) => {
    const newFiles = [...files];
    newFiles.splice(index, 1);
    setFiles(newFiles);
  };

  useEffect(() => {
    if (data) {
      setPartName(data.partName);
      setDirection(data.direction);
      setRubric({
        rubricNo: data.rubricNo,
        rubricName: data.rubricName,
        useYN: 'Y',
      });
      setAttachFiles(data.attachFiles);
    }
  }, [data]);

  const onDeleteAttachedFile = (index: number) => {
    const newAttachFiles = [...attachFiles];
    newAttachFiles[index].originalName = null;
    setAttachFiles(newAttachFiles);
  };

  return (
    <>
      <div className='comm-bx__between'>
        <h4 className='sub-title'>{title}</h4>
      </div>
      <div className='comm-table__area rowType'>
        <Descriptions bordered labelStyle={{ textAlign: 'center' }} column={1}>
          <Descriptions.Item label={t('cbt.speakTest.column1')}>
            <input
              type="text"
              className="form-control"
              value={partName}
              onChange={(e) => setPartName(e.target.value)}
            />
          </Descriptions.Item>
          <Descriptions.Item label={t('cbt.speakTest.column9')}>
            <input
              type="text"
              className="form-control"
              value={direction}
              onChange={(e) => setDirection(e.target.value)}
            />
          </Descriptions.Item>
          {attachFiles?.length && (
            <Descriptions.Item label={t('cbt.speakTest.column11')}>
              {attachFiles.map(({ fileLocation, originalName }, index) => originalName && (
                <div className="mb-2" key={`image-${index}`}>
                  <Image src={fileLocation} />
                  <span className="ml-1 pointer" onClick={() => onDeleteAttachedFile(index)}>
                    <CgCloseR />
                  </span>
                </div>
              ))}
            </Descriptions.Item>
          )}
          <Descriptions.Item label={data ? t('cbt.speakTest.column10') : t('cbt.speakTest.column12')}>
            {files.length > 0 &&
              files.map((file, index) => (
                <div key={`file-${index}`} className="flex mb-2">
                  <FileUpload
                    file={file}
                    setFile={(file: File) => onSetFile(index, file)}
                  />
                  <div className="comm-btn__area ml-2">
                    <Button
                      className="btn-basic small gray"
                      onClick={() => onDeleteFile(index)}
                      text={t('common.button.delete')}
                      writeAuthYn={writeAuthYn}
                    />
                  </div>
                </div>
              ))}
            <div className="pr-t1 comm-btn__area">
              <Button
                className="btn-basic pink-line"
                onClick={onAddFile}
                text={t('common.button.add')}
                writeAuthYn={writeAuthYn}
              />
            </div>
          </Descriptions.Item>
          <Descriptions.Item label={t('cbt.speakTest.column8')}>
            <div className="comm-btn__area align-items-center">
              {rubric?.rubricName}
              <Button
                className={`btn-basic black small`}
                onClick={() => setVisible(true)}
                text={t('cbt.speakTest.button3')}
              />
            </div>
          </Descriptions.Item>
        </Descriptions>
      </div>
      <div className="comm-bx__between">
        <div className="comm-btn__area">
          <Button
            className="btn-basic gray-line"
            onClick={() =>
              history.push(`/cbt/speaking-test/${speakingExamCode}/edit`)
            }
            text={t('common.button.back')}
          />
        </div>
        <div className="comm-btn__area right">
          <Button
            className="btn-basic pink"
            onClick={onSubmit}
            text={t('common.button.save')}
            writeAuthYn={writeAuthYn}
          />
        </div>
      </div>
      {visible && (
        <SelectSpeakingItemModal
          visible={visible}
          onOk={onOk}
          onCancel={onCancel}
        />
      )}
    </>
  );
};

export default SpeakingTestPartEditor;
